import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as PartnerProjectActions from './projects.actions';
import * as _ from 'lodash';
import { PartnerProject } from './../../../app/@shared/models/partner_project.model';
import { IMeta, IRequestOptions } from '../request-options.interface';

export const featureKey = '[partner_projects]';

export const adapter: EntityAdapter<PartnerProject> = createEntityAdapter<PartnerProject>({
  selectId: (a => a.object_uuid!)!, // define "primary id"
  sortComparer: false
});

export const listAdapter: EntityAdapter<PartnerProject> = createEntityAdapter<PartnerProject>({
  selectId: (a => a.object_uuid!)!, // define "primary id"
  sortComparer: false
});

export const listForLocationAdapter: EntityAdapter<PartnerProject> = createEntityAdapter<PartnerProject>({
  selectId: (a => a.object_uuid!)!, // define "primary id"
  sortComparer: false
});

const listAdapterInitialState = listAdapter.getInitialState({
  listConfig: {
    limit: 10,
    sort: '-name',
    fields: 'name,contract_name,hybrid_core_uuid,contract_uuid'
  },
  loaded: false,
  loading: false,
  loadingNext: false
}) as ListState;

export const listForLocationAdapterInitialState = listForLocationAdapter.getInitialState({
  listConfig: {
    limit: 10,
    sort: '-name',
    fields: 'name,contract_name,hybrid_core_uuid,contract_uuid'
  },
  loaded: false,
  loading: false,
  loadingNext: false
}) as ListState;

export const initialState: State = adapter.getInitialState({
  loading: {},
  loaded: {},
  patching: {},
  deleting: {},
  watching: {},
  list: listAdapterInitialState,
  listForLocation: {}
});

export interface ListState extends EntityState<PartnerProject> {
  listConfig: IRequestOptions;
  loaded: boolean;
  loading: boolean;
  loadingNext: boolean;
  search?: string;
  meta?: IMeta;
  error?: Error;
  links?: {
    next?: {
      href: string
    }
  }
}

export interface State extends EntityState<PartnerProject> {
  loading: { [key: string]: boolean };
  loaded: { [key: string]: boolean };
  patching: { [key: string]: boolean };
  deleting: { [key: string]: boolean };
  watching: { [key: string]: boolean };
  list: ListState;
  listForLocation: Record<string, ListState>;
}

export const reducer = createReducer(
  initialState,

  on(PartnerProjectActions.setListConfig, (state, action) => {
    if (_.isEqual(_.get(state, 'list.listConfig', {}), action.config)) {
      return state;
    }
    return {
      // update list config / empty collection
      ...state,

      list: listAdapter.removeAll({
        ...state.list,
        loaded: false,
        search: undefined,
        listConfig: {
          // ...state.listConfig,
          ...action.config
        }
      })
    };
  }),

  on(PartnerProjectActions.updateListConfig, (state, action) => {
    if (_.isEqual(_.get(state, 'list.listConfig', {}), action.config)) {
      return state;
    }

    const currentConfig = JSON.parse(JSON.stringify(_.get(state, 'list.listConfig', {})));

    return {
      // update list config / empty collection
      ...state,

      list: {
        ...state.list,
        loaded: false,
        listConfig: _.merge(currentConfig, action.config)
      }
    };
  }),

  on(PartnerProjectActions.setSearch, (state, action) => {
    if (_.isEqual(_.get(state, 'list.listConfig.filter', ''), action.search)) {
      return state;
    }

    const currentFilter = _.filter(
      _.get(state, 'list.listConfig.filter', '').split(','),
      f => f.indexOf('name~~') === -1
    ).join(',');


    return {
      // update list config / empty collection
      ...state,

      list: listAdapter.removeAll({
        ...state.list,
        loaded: false,
        search: action.search,
        listConfig: {
          ..._.get(state, 'list.listConfig', {}),
          page: 0,
          filter: _.isEmpty(action.search) ? currentFilter : (_.isEmpty(currentFilter) ? '' : currentFilter + ',') + 'name~~*%%' + action.search + '%%'
        }
      })
    };
  }),


  on(PartnerProjectActions.loadPartnerProjects, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: true,
      loadingNext: false,
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsSuccess, (state: State, action) => ({
    // set new entities (clears collection)
    ...state,

    list: listAdapter.setAll(_.toArray(action.data), {
      ...state.list,
      meta: action.meta,
      links: action.links,
      loading: false,
      loaded: true,
      error: undefined
    })
  })),

  on(PartnerProjectActions.loadPartnerProjectsFailure, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      entities: {},
      loading: false,
      loaded: true,
      error: action.error
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsNext, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: _.get(state, ['list', 'links', 'next']) !== undefined ? true : false,
      loadingNext: _.get(state, ['list', 'links', 'next']) !== undefined ? true : false
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsNextSuccess, (state: State, action) => ({
    // upsert entities when next page loaded...
    ...state,

    list: listAdapter.upsertMany(_.toArray(action.data), {
      ...state.list,
      meta: action.meta,
      links: action.links,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: undefined
    })
  })),

  on(PartnerProjectActions.loadPartnerProjectsNextFailure, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: action.error
    }
  })),

  on(PartnerProjectActions.loadPartnerProject, (state, action) => ({
    ...state,
    loading: {
      ..._.get(state, 'loading', {}),
      [action.uuid]: true
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectSuccess, (state, action) => adapter.setOne(action.data, {
    ...state,
    loading: {
      ..._.get(state, 'loading', {}),
      [action.uuid]: false
    },
    loaded: {
      ..._.get(state, 'loaded', {}),
      [action.uuid]: true
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectFailure, (state, action) => ({
    ...state,
    loading: {
      ..._.get(state, 'loading', {}),
      [action.uuid]: false
    },
  })),







  on(PartnerProjectActions.loadPartnerProjectsForLocation, (state, action) => ({
    ...state,

    listForLocation: {
      ...state.listForLocation,
      [action.locationUuid]: {
        ..._.get(state.listForLocation, action.locationUuid, listForLocationAdapterInitialState),
        loading: true,
        loadingNext: false,
      }
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsForLocationSuccess, (state: State, action) => ({
    // set new entities (clears collection)
    ...state,

    listForLocation: {
      ...state.listForLocation,
      [action.locationUuid]: listForLocationAdapter.setAll(_.toArray(action.data), {
        ..._.get(state.listForLocation, action.locationUuid, listForLocationAdapterInitialState),
        meta: action.meta,
        links: action.links,
        loading: false,
        loaded: true,
        error: undefined
      })
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsForLocationFailure, (state, action) => ({
    ...state,

    listForLocation: {
      ...state.listForLocation,
      [action.locationUuid]: {
        ..._.get(state.listForLocation, action.locationUuid, listForLocationAdapterInitialState),
        entities: {},
        loading: false,
        loaded: true,
        error: action.error
      }
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsForLocationNext, (state, action) => ({
    ...state,

    listForLocation: {
      ...state.listForLocation,
      [action.locationUuid]: {
        ..._.get(state.listForLocation, action.locationUuid, listForLocationAdapterInitialState),
        loading: _.get(state, ['listForLocation', 'links', 'next']) !== undefined ? true : false,
        loadingNext: _.get(state, ['listForLocation', 'links', 'next']) !== undefined ? true : false
      }
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsForLocationNextSuccess, (state: State, action) => ({
    // upsert entities when next page loaded...
    ...state,

    listForLocation: {
      ...state.listForLocation,
      [action.locationUuid]: listForLocationAdapter.upsertMany(_.toArray(action.data), {
        ..._.get(state.listForLocation, action.locationUuid, listForLocationAdapterInitialState),
        meta: action.meta,
        links: action.links,
        loading: false,
        loadingNext: false,
        loaded: true,
        error: undefined
      })
    }
  })),

  on(PartnerProjectActions.loadPartnerProjectsForLocationNextFailure, (state, action) => ({
    ...state,

    listForLocation: {
      ...state.listForLocation,
      [action.locationUuid]: {
        ..._.get(state.listForLocation, action.locationUuid, listForLocationAdapterInitialState),
        loading: false,
        loadingNext: false,
        loaded: true,
        error: action.error
      }
    }
  })),
);
